import React, { FunctionComponent } from 'react'
import { trackUserEvent } from '@vend/utilities'
import { Button, Text } from '@lightspeed/design-system-react'
import { T } from '@transifex/react'
import { useFormatCurrencyValue } from '../summary-panel/useFormatCurrencyValue'
import {
  getPaymentsTotal,
  isPartialPaidSale,
  isUnpaidSale,
} from '../../../functions/sale'
import {
  saleSubmittedEarly,
  selectSaleLocalId,
  selectSaleSummaryFields,
  selectPayAccountBalance,
} from '../../../state/slices/currentSale/currentSaleSlice'
import { WEB_REGISTER } from '../../web-register.constant'
import { useAppDispatch, useAppSelector } from '../../../state/hooks'
import { useSaleManager } from '../../../ng-compat/hooks'
import { useFulfillmentTakeNowSaleData } from '../useFulfillmentTakeNowSaleData'
import { getFeatureFromCache } from '../../../util/feature-cache'
import { FEATURE_FULFILLMENT_TAKE_NOW_ITEM_PAYMENT_ENFORCEMENT } from '../../../constants'
import { useGoConfirmation } from './hooks/useGoConfirmation'

export const SaveSale: FunctionComponent = () => {
  const saleSummary = useAppSelector(selectSaleSummaryFields)
  const dispatch = useAppDispatch()
  const saleID = useAppSelector(selectSaleLocalId)
  const saleManager = useSaleManager()
  const formatCurrency = useFormatCurrencyValue()
  const goToConfirmation = useGoConfirmation()
  const depositAmount = getPaymentsTotal(saleSummary.payments)
  const payAccountBalance = useAppSelector(selectPayAccountBalance)
  const {
    takeNowItemsBalance,
    shouldShowComplexSaleBalanceBreakdown: disableSaveSaleForUnpaidTakeNowItems,
  } = useFulfillmentTakeNowSaleData()

  // Sale-less ABP: no backend sale — "Save Sale" does not apply in this flow.
  if (payAccountBalance?.abpTransactionId) return null

  const isTakeNowPaymentEnforcementFeatureEnabled = getFeatureFromCache(
    FEATURE_FULFILLMENT_TAKE_NOW_ITEM_PAYMENT_ENFORCEMENT
  )
  const handleSaveSale = () => {
    if (disableSaveSaleForUnpaidTakeNowItems) {
      return
    }
    trackUserEvent(WEB_REGISTER.DISPLAY_NAME, 'Clicked: Save Sale', {
      id: saleID,
      paymentsTotal: getPaymentsTotal(saleSummary.payments),
      saleTotal: saleSummary.finals.inclusivePrice,
    })
    saleManager.closeSale().then(() => {
      if (saleManager.shouldSubmitSaleEarly()) {
        dispatch(saleSubmittedEarly())
        saleManager.submitSale()
      }
      goToConfirmation()
    })
  }

  return (
    <>
      {isTakeNowPaymentEnforcementFeatureEnabled ? (
        <Text
          data-testid="save-sale-disabled-message"
          className="save-sale-disabled-message vd-flex vd-flex--align-center"
        >
          {disableSaveSaleForUnpaidTakeNowItems ? (
            <T
              _str="Pay {amount} for the take now items to save the sale. Additional payments will be added as a deposit."
              _context="Save sale message"
              amount={formatCurrency(takeNowItemsBalance)}
            />
          ) : (
            <T
              _str="You can save the sale or take additional payments as a deposit."
              _context="Save sale message"
            />
          )}
        </Text>
      ) : (
        <>
          {isPartialPaidSale(saleSummary) && (
            <div className="vd-flex vd-flex--align-center">
              <T
                _str="{deposit} will be applied as a deposit."
                _context="Save sale message"
                deposit={formatCurrency(depositAmount)}
              />
            </div>
          )}

          {isUnpaidSale(saleSummary) && (
            <div className="vd-flex vd-flex--align-center">
              <T
                _str="No deposit will be applied."
                _context="Save sale message"
              />
            </div>
          )}
        </>
      )}

      <Button
        type="button"
        variant="supplementary"
        jumbo
        onClick={handleSaveSale}
        data-track="save-sale"
        disabled={disableSaveSaleForUnpaidTakeNowItems}
      >
        <T _str="Save Sale" _context="save sale button" />
      </Button>
    </>
  )
}
